// ==================================
// Pagina de Login
// ==================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, Input } from '@/componentes/ui';
import { authServicio } from '@/servicios';
import { useAuthStore } from '@/estado/authStore';

// Iconos inline
const EmailIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const LockIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
  </svg>
);

export function Login() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const loginMutation = useMutation({
    mutationFn: () => authServicio.login({ email, password }),
    onSuccess: (data) => {
      setAuth(data.usuario, data.accessToken, data.refreshToken);
      toast.success('¡Bienvenido!');
      navigate('/');
    },
    onError: () => {
      toast.error('Credenciales incorrectas');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Completa todos los campos');
      return;
    }
    loginMutation.mutate();
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branding con imagen de fondo */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center">
        {/* Imagen de fondo */}
        <img
          src="/polygon.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay oscuro para legibilidad */}
        <div className="absolute inset-0 bg-obsidian-950/60" />
        {/* Glow central sutil */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary-600/8 rounded-full blur-3xl" />

        <div className="relative flex flex-col items-center">
          <img
            src="/logo-sin-fondo-blanco.png"
            alt="Alebrije Cloud"
            className="w-72 h-72 object-contain mb-8 drop-shadow-2xl"
          />
          <h1 className="font-display font-bold text-5xl tracking-tight drop-shadow-lg">
            <span className="text-primary-400">Alebrije</span>{' '}
            <span className="text-white">Cloud</span>
          </h1>
          <p className="text-stone-300/80 text-lg mt-4 text-center max-w-xs">
            Plataforma de gestión veterinaria
          </p>

          {/* Linea decorativa greca */}
          <div className="mt-10 flex items-center gap-3">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-primary-400/40" />
            <div className="w-2 h-2 rotate-45 border border-primary-400/40" />
            <div className="w-2 h-2 rotate-45 bg-primary-400/30" />
            <div className="w-2 h-2 rotate-45 border border-primary-400/40" />
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-primary-400/40" />
          </div>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex flex-col bg-white">
        <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-8 py-8">
          <div className="w-full max-w-sm">
            {/* Logo solo en movil */}
            <div className="lg:hidden flex flex-col items-center mb-8">
              <img
                src="/logo-sinletras-blanco.png"
                alt="Alebrije Cloud"
                className="h-44 object-contain mb-3"
              />
              <h1 className="text-xl font-display font-bold text-obsidian-900">
                <span className="text-primary-600">Alebrije</span> Cloud
              </h1>
            </div>

            {/* Heading */}
            <div className="mb-8">
              <h2 className="text-2xl font-display font-bold text-obsidian-900">
                Bienvenido de vuelta
              </h2>
              <p className="text-stone-400 text-sm mt-2">
                Ingresa tus credenciales para acceder al panel
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@alebrijecloud.com"
                autoComplete="email"
                icon={<EmailIcon />}
              />

              <Input
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingresa tu contraseña"
                autoComplete="current-password"
                icon={<LockIcon />}
                suffix={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-stone-400 hover:text-primary-600 transition-colors p-0.5 rounded-md focus:outline-none focus:text-primary-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                }
              />

              <div className="pt-1">
                <Button
                  type="submit"
                  loading={loginMutation.isPending}
                  className="w-full"
                  size="lg"
                >
                  {loginMutation.isPending ? 'Ingresando...' : 'Iniciar sesión'}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="py-6 text-center">
          <p className="text-xs text-stone-300">
            Solo usuarios autorizados
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
